import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card } from '../../components/ui/Card.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';

interface CarRevenueRow {
  car_id: string;
  label: string;
  plate_number: string | null;
  gross_minor: number;
  platform_fee_minor: number;
  net_minor: number;
  booking_count: number;
}

interface RevenueTotals {
  gross_minor: number;
  platform_fee_minor: number;
  net_minor: number;
  booking_count: number;
}

interface RevenueByCarResponse {
  month: string;
  cars: CarRevenueRow[];
  totals: RevenueTotals;
}

// 377 (#1233): money is the supplier's, so there is no "By Host" grouping (303 US3's `group_by=host` is gone).

// Each admin page keeps its own copy rather than a shared util — DashboardPage,
// BookingListPage and BookingDetailPage already do this independently.
function formatMyr(minor: number) {
  return `RM ${(minor / 100).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, delta: number): string {
  const [yearStr, monthStr] = month.split('-');
  const d = new Date(Date.UTC(Number(yearStr), Number(monthStr) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// The per-car table.
function CarRevenueTable({
  cars,
  totals,
  totalLabel = 'Total',
}: {
  cars: CarRevenueRow[];
  totals: RevenueTotals;
  totalLabel?: string;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-hairline text-left text-steel">
          <th className="py-2 pr-4 font-medium">Car</th>
          <th className="py-2 pr-4 font-medium">Plate</th>
          <th className="py-2 pr-4 font-medium text-right">Rentals</th>
          <th className="py-2 pr-4 font-medium text-right">Gross</th>
          <th className="py-2 pr-4 font-medium text-right">Platform fee</th>
          <th className="py-2 pr-4 font-medium text-right">Net</th>
        </tr>
      </thead>
      <tbody>
        {cars.map((row) => (
          <tr key={row.car_id} className="border-b border-hairline-soft">
            <td className="py-2 pr-4 text-ink">{row.label}</td>
            <td className="py-2 pr-4 text-steel">{row.plate_number ?? '—'}</td>
            <td className="py-2 pr-4 text-right text-ink">{row.booking_count}</td>
            <td className="py-2 pr-4 text-right text-ink">{formatMyr(row.gross_minor)}</td>
            <td className="py-2 pr-4 text-right text-steel">{formatMyr(row.platform_fee_minor)}</td>
            <td className="py-2 pr-4 text-right font-semibold text-ink">
              {formatMyr(row.net_minor)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-hairline font-semibold">
          <td className="py-2 pr-4 text-ink" colSpan={2}>
            {totalLabel}
          </td>
          <td className="py-2 pr-4 text-right text-ink">{totals.booking_count}</td>
          <td className="py-2 pr-4 text-right text-ink">{formatMyr(totals.gross_minor)}</td>
          <td className="py-2 pr-4 text-right text-ink">{formatMyr(totals.platform_fee_minor)}</td>
          <td className="py-2 pr-4 text-right text-ink">{formatMyr(totals.net_minor)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

export function RevenueByCarPage() {
  const [month, setMonth] = useState(currentMonth);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['revenue-by-car', month],
    queryFn: () => api.get<RevenueByCarResponse>(`/admin/revenue-by-car?month=${month}`),
    staleTime: 60_000,
    retry: false,
  });

  const status = (error as { status?: number } | null)?.status;
  const isRefused = status === 403 || status === 401;

  return (
    <div>
      <PageHeader
        title="Revenue by Car"
        description="Paid bookings by pickup date (Malaysia time), per car, after the platform fee. Revenue is counted as in the Sales Statement."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous month"
              className="rounded-md border border-hairline px-2 py-1 text-sm text-steel hover:text-ink"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
            >
              ‹
            </button>
            <label className="sr-only" htmlFor="revenue-month">
              Month
            </label>
            <input
              id="revenue-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-md border border-hairline px-2 py-1 text-sm text-ink"
            />
            <button
              type="button"
              aria-label="Next month"
              className="rounded-md border border-hairline px-2 py-1 text-sm text-steel hover:text-ink"
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
            >
              ›
            </button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : isError ? (
        <Card>
          <p className="text-sm text-steel">
            {isRefused
              ? 'Platform revenue figures are not available for your account.'
              : 'Revenue could not be loaded. Please try again shortly.'}
          </p>
        </Card>
      ) : data?.cars.length === 0 ? (
        <Card>
          <p className="text-sm text-steel">No paid bookings were picked up in this month.</p>
        </Card>
      ) : data ? (
        <Card className="overflow-x-auto">
          <CarRevenueTable cars={data.cars} totals={data.totals} />
        </Card>
      ) : null}
    </div>
  );
}
